import type { OrderDraftLine } from './orderDraftModel';
import {
  asQuantityUnit,
  buildQuantitySetFromPair,
  getQuantitySetValue,
  hasPersistedQuantitySet,
  isAllocationUnit,
  quantitySetFromFields,
  quantitySetToFields,
  type CommercialQuantityValue,
} from '../../../../utils/commercialQuantity';

/**
 * Unit conversion and currency of a Szkic line live on the line itself (the
 * „Dodaj produkt” row and the lines table), not in „Więcej danych”. The pair
 * and the quantity set are the same ones the „Sprzedaż” editor uses
 * (`CommercialQuantityEditor`, docs/modules/products-and-units.md).
 */

/** The pair the unit-conversion editor shows for a line. */
export function getOrderDraftLineQuantityValue(line: OrderDraftLine): CommercialQuantityValue {
  const unit = asQuantityUnit(line.unit);
  return {
    physicalQuantity: Number(line.quantity) || 0,
    physicalUnit: unit,
    pricingQuantity: Number(line.saleQuantity ?? line.quantity) || 0,
    pricingUnit: asQuantityUnit(line.saleUnit || line.unit),
    conversionQuantity: line.conversionQuantity,
    conversionUnit: line.conversionUnit,
    quantityPcs: line.quantityPcs ?? null,
    quantityM2: line.quantityM2 ?? null,
    quantityM3: line.quantityM3 ?? null,
  };
}

/** The accepted pair becomes the new quantity set of the line (same rule as the „Sprzedaż” editor). */
export function applyQuantityPairToOrderDraftLine(line: OrderDraftLine, next: CommercialQuantityValue): OrderDraftLine {
  const nextSet = buildQuantitySetFromPair(next, line as never);
  return {
    ...line,
    quantity: next.physicalQuantity,
    unit: next.physicalUnit,
    saleQuantity: next.pricingQuantity,
    saleUnit: next.pricingUnit,
    conversionQuantity: next.conversionQuantity,
    conversionUnit: next.conversionUnit,
    ...(nextSet ? quantitySetToFields(nextSet) : {}),
  };
}

/** A product counted in pieces only has nothing to convert. */
export const canConvertOrderDraftLine = (line: Pick<OrderDraftLine, 'countInPiecesOnly'>): boolean => line.countInPiecesOnly !== true;

/** The second unit a line is shown in (e.g. 155 szt. → 24 m²), or null. */
export function getOrderDraftLineConversion(line: OrderDraftLine): { unit: string; quantity: number | null } | null {
  const unit = line.conversionUnit ?? (line.saleUnit && line.saleUnit !== line.unit ? line.saleUnit : null);
  if (!unit || unit === line.unit) return null;
  let quantity: number | null = null;
  if (isAllocationUnit(unit)) {
    if (hasPersistedQuantitySet(line as unknown as Record<string, unknown>)) {
      const set = quantitySetFromFields(line as unknown as Record<string, unknown>);
      quantity = set ? getQuantitySetValue(set, unit) ?? null : null;
    }
  }
  if (quantity == null) {
    quantity = line.conversionUnit === unit && line.conversionQuantity != null
      ? Number(line.conversionQuantity)
      : (line.saleUnit === unit && line.saleQuantity != null ? Number(line.saleQuantity) : null);
  }
  return { unit, quantity: quantity != null && Number.isFinite(quantity) ? quantity : null };
}
